import {
  AspectRatio,
  Avatar,
  Box,
  Divider,
  Flex,
  Heading,
  HStack,
  Image,
  Text,
} from "@highoutput/ui-components";
import { format } from "date-fns";
import { useMemo } from "react";
import type { PostCoreDataFragment } from "~/__generated__/graphql";
import Footer from "../landing/v2/footer";
import Navbar from "../navbar";
import Show from "../utilities/show";
import { useMarkdownStyle } from "./hooks";
import MainContent from "./main-content";
import ShareButtons from "./share-buttons";
import ViewCounter from "./view-counter";

export default function BlogDetails({ data }: { data: PostCoreDataFragment }) {
  const shareableLink = useMemo(() => {
    return `https://advante.co/blog/${data.attributes?.slug}`;
  }, [data.attributes?.slug]);

  return (
    <Flex direction="column" minHeight="100vh">
      <Navbar />

      <Box
        as="main"
        paddingBottom={{
          base: "64px",
          lg: "128px",
        }}
        flexGrow={1}
        fontFamily="'Inter', sans-serif"
      >
        <Box>
          <Box
            bgColor="rgba(240,237,236,0.2)"
            paddingTop={{
              base: "40px",
              lg: "100px",
            }}
            paddingBottom={{
              base: "40px",
              lg: "64px",
            }}
            paddingX={{
              base: "16px",
              lg: "24px",
              xl: "unset",
            }}
          >
            {/* <!-- Title --> */}
            <Heading
              as="h1"
              variant={{
                base: "32/36",
                lg: "56/64",
              }}
              letterSpacing={{
                base: "-0.015em",
                lg: "unset",
              }}
              textAlign="center"
              marginX="auto"
              maxWidth={{
                base: "375px",
                lg: "1024px",
                xl: "960px",
              }}
              paddingX={{
                base: "0.4rem",
                lg: "1rem",
                xl: "unset",
              }}
              data-testid="post.title"
              fontWeight={400}
            >
              {data.attributes?.title}
            </Heading>

            {/* <!-- Description --> */}
            <Show when={!!data.attributes?.description}>
              <Text
                variant={{
                  base: "16/24",
                  lg: "24/36",
                }}
                color="rgba(13,48,80,0.8)"
                marginTop={{
                  base: "16px",
                  lg: "24px",
                }}
                textAlign="center"
                marginX="auto"
                maxWidth={{
                  base: "375px",
                  lg: "1024px",
                  xl: "960px",
                }}
                data-testid="post.description"
              >
                {data.attributes?.description}
              </Text>
            </Show>

            {/* <!-- Author --> */}
            <HStack
              spacing="12px"
              width="fit-content"
              marginX="auto"
              marginTop={{
                base: "32px",
                lg: "40px",
              }}
            >
              <Avatar
                size="md"
                src={data.attributes?.author?.data?.attributes?.avatar?.data?.attributes?.url}
                name={data.attributes?.author?.data?.attributes?.name ?? "unknown"}
              />

              <Box>
                <Text
                  color="rgba(13,48,80,0.8)"
                  variant="18/18"
                  fontWeight="medium"
                  data-testid={`posts.list.${data.id}.author`}
                >
                  {data.attributes?.author?.data?.attributes?.name ?? "unknown"}
                </Text>
                <Text variant="18/18" marginTop="4px" color="rgba(13,48,80,0.6)" display="none">
                  {format(new Date(data.attributes?.publishedAt), "LL MMM yyyy")}
                </Text>
              </Box>
            </HStack>
          </Box>

          {/* <!-- Cover --> */}
          <Box
            position="relative"
            paddingX={{
              base: "16px",
              lg: "24px",
              xl: "unset",
            }}
          >
            <Box
              width="full"
              height={{
                base: "calc(240px - 152px)",
                lg: "calc(616px - 388px)",
              }}
              bgColor="rgba(240,237,236,0.2)"
              position="absolute"
              top={0}
              left={0}
            />
            <AspectRatio ratio={16 / 9} maxWidth="1216px" width="full" marginX="auto">
              <Image
                src={data.attributes?.cover.data?.attributes?.url}
                alt="Cover"
                data-testid="post.cover"
              />
            </AspectRatio>
          </Box>

          {/* <!-- Content (in markdown) --> */}
          <Box
            minHeight="400px"
            marginTop={{
              base: "32px",
              lg: "80px",
            }}
            data-testid="post.content"
            marginX="auto"
            maxWidth={{
              base: "full" /* just change this to 375px if necessary 👌 */,
              lg: "1024px",
              xl: "960px",
            }}
            paddingX={{
              base: "16px",
              lg: "24px",
              xl: "unset",
            }}
            sx={useMarkdownStyle()}
            fontFamily="'Inter', sans-serif"
          >
            <MainContent markdown={data.attributes?.content ?? ""} />
            <Divider
              m="auto"
              marginTop={{
                base: "40px",
                lg: "60px",
              }}
            />
          </Box>

          {/* <!-- Social Share --> */}
          <Flex
            paddingX={{
              base: "16px",
              lg: "24px",
              xl: "unset",
            }}
            maxWidth="960px"
            marginX="auto"
            alignItems={{
              lg: "center",
            }}
            justifyContent="space-between"
            paddingTop="24px"
            direction={{
              base: "column",
              lg: "row",
            }}
            gap="24px"
          >
            <Text variant="18/18" color="rgba(13,48,80,0.6)" fontWeight="medium">
              Share this post
            </Text>

            <ShareButtons url={shareableLink} />
          </Flex>
        </Box>
      </Box>

      <Footer cta />

      {data.id && <ViewCounter id={data.id} />}
    </Flex>
  );
}
